import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, BedDouble, Users, Receipt,
  Wallet, Gauge, AlertCircle, BarChart3, FileCheck2, ScrollText, LogOut,
  Menu, X,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Toaster } from "../components/ui/sonner";

const navItems = [
  { to: "/dashboard", end: true, label: "Overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/dashboard/properties", label: "Properties", icon: Building2, testid: "nav-properties" },
  { to: "/dashboard/rooms", label: "Rooms", icon: BedDouble, testid: "nav-rooms" },
  { to: "/dashboard/tenants", label: "Tenants", icon: Users, testid: "nav-tenants" },
  { to: "/dashboard/rent", label: "Rent", icon: Receipt, testid: "nav-rent" },
  { to: "/dashboard/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses" },
  { to: "/dashboard/meters", label: "Electricity", icon: Gauge, testid: "nav-meters" },
  { to: "/dashboard/complaints", label: "Complaints", icon: AlertCircle, testid: "nav-complaints" },
  { to: "/dashboard/reports", label: "Reports", icon: BarChart3, testid: "nav-reports" },
  { to: "/dashboard/documents", label: "Documents", icon: FileCheck2, testid: "nav-documents" },
  { to: "/dashboard/audit", label: "Audit log", icon: ScrollText, testid: "nav-audit" },
];

function BrandLogo({ testid, compact }) {
  if (compact) {
    return (
      <Link to="/dashboard" data-testid={testid} className="font-display text-xl font-black tracking-tighter hover:opacity-80 transition-opacity">
        Stay<span className="text-blue-500">Care</span>
      </Link>
    );
  }
  return (
    <Link to="/dashboard" data-testid={testid} className="hover:opacity-80 transition-opacity">
      <div className="font-display text-3xl font-black text-white tracking-tighter">
        Stay<span className="text-blue-500">Care</span>
      </div>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mt-1">Property OS</div>
    </Link>
  );
}

function MobileTopBar({ onOpen }) {
  return (
    <header className="lg:hidden fixed top-0 inset-x-0 z-30 h-14 bg-slate-900 text-white flex items-center justify-between px-4 border-b border-slate-800">
      <BrandLogo testid="dashboard-logo-mobile" compact />
      <button
        onClick={onOpen}
        data-testid="open-sidebar-btn"
        aria-label="Open menu"
        className="h-11 w-11 inline-flex items-center justify-center hover:bg-slate-800"
      >
        <Menu className="h-5 w-5" />
      </button>
    </header>
  );
}

function SidebarNav() {
  return (
    <nav className="flex-1 py-4 space-y-0.5 overflow-y-auto">
      {navItems.map(({ to, end, label, icon: Icon, testid }) => (
        <NavLink
          key={to}
          to={to}
          end={end}
          data-testid={testid}
          className={({ isActive }) =>
            `flex items-center gap-3 px-6 py-3 text-sm transition-colors min-h-[44px] ${
              isActive
                ? "bg-blue-600 text-white font-medium border-l-4 border-blue-300"
                : "hover:bg-slate-800 hover:text-white border-l-4 border-transparent"
            }`
          }
        >
          <Icon className="h-4 w-4 shrink-0" />
          <span className="truncate">{label}</span>
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarFooter({ email, onLogout }) {
  return (
    <div className="p-4 border-t border-slate-800">
      <div className="text-xs text-slate-400 mb-2 truncate" data-testid="user-email">{email}</div>
      <button
        onClick={onLogout}
        data-testid="logout-btn"
        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm bg-slate-800 hover:bg-slate-700 text-white transition-colors min-h-[44px]"
      >
        <LogOut className="h-4 w-4" /> Sign out
      </button>
    </div>
  );
}

function Sidebar({ open, onClose, email, onLogout }) {
  return (
    <aside
      className={`fixed lg:static top-0 bottom-0 left-0 z-50 w-72 lg:w-64 bg-slate-900 text-slate-300 flex flex-col transition-transform duration-200 ease-out ${
        open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
      }`}
      data-testid="dashboard-sidebar"
    >
      <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
        <BrandLogo testid="dashboard-logo" />
        <button
          onClick={onClose}
          data-testid="close-sidebar-btn"
          aria-label="Close menu"
          className="lg:hidden h-11 w-11 -mr-2 inline-flex items-center justify-center text-slate-400 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      <SidebarNav />
      <SidebarFooter email={email} onLogout={onLogout} />
    </aside>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when route changes (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Lock body scroll when sidebar is open on mobile
  useEffect(() => {
    document.body.style.overflow = sidebarOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [sidebarOpen]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-dvh flex bg-slate-50" data-testid="dashboard-layout">
      <MobileTopBar onOpen={() => setSidebarOpen(true)} />

      {/* Backdrop for mobile sidebar */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          className="lg:hidden fixed inset-0 bg-black/50 z-40 animate-fade-in-up"
          aria-hidden="true"
        />
      )}

      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)}
               email={user?.email} onLogout={handleLogout} />

      {/* Main */}
      <main className="flex-1 min-w-0 overflow-x-hidden pt-14 lg:pt-0">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
          <Outlet />
        </div>
        <Toaster />
      </main>
    </div>
  );
}
